import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shell } from "../components/Shell";
import { Spinner } from "../components/Icons";
import { api } from "../lib/api";
import type { Presentation } from "../lib/types";

function relative(iso: string): string {
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const then = new Date(hasZone ? iso : `${iso}Z`).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export default function Decks() {
  const navigate = useNavigate();
  const [decks, setDecks] = useState<Presentation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api
      .listPresentations()
      .then(setDecks)
      .catch((err) => setError(err.message));
  }, []);

  useEffect(load, [load]);

  async function create(seedDemo: boolean) {
    setCreating(true);
    try {
      const deck = await api.createPresentation(
        seedDemo ? "AI at work" : "Untitled presentation",
        seedDemo,
      );
      navigate(`/admin/${deck.id}`);
    } catch (err) {
      setError((err as Error).message);
      setCreating(false);
    }
  }

  async function remove(deck: Presentation) {
    if (!confirm(`Delete "${deck.title}" and all its slides?`)) return;
    await api.deletePresentation(deck.id);
    load();
  }

  return (
    <Shell
      actions={
        <>
          <button className="btn btn-outline" disabled={creating} onClick={() => create(true)}>
            New from demo
          </button>
          <button className="btn" disabled={creating} onClick={() => create(false)}>
            New presentation
          </button>
        </>
      }
    >
      <div style={{ maxWidth: 840, margin: "0 auto", padding: "32px 20px" }}>
        <h1 style={{ fontSize: 26, marginBottom: 6 }}>Your presentations</h1>
        <p className="muted" style={{ marginBottom: 26 }}>
          Each one can be presented any number of times. Every run keeps its own results.
        </p>

        {error && (
          <p role="alert" className="card" style={{ color: "var(--error)", marginBottom: 16 }}>
            {error}
          </p>
        )}

        {decks === null && <Spinner size={24} />}

        {decks?.length === 0 && (
          <div className="card" style={{ textAlign: "center", padding: 40 }}>
            <h2 style={{ fontSize: 17, marginBottom: 6 }}>Nothing here yet</h2>
            <p className="muted" style={{ marginBottom: 20 }}>
              Start from the seven-slide demo deck to see all eight question types at once.
            </p>
            <button className="btn" onClick={() => create(true)}>
              Create the demo deck
            </button>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {decks?.map((deck) => (
            <div
              key={deck.id}
              className="card"
              style={{ display: "flex", alignItems: "center", gap: 16, padding: "18px 22px" }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link
                  to={`/admin/${deck.id}`}
                  style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}
                >
                  {deck.title}
                </Link>
                <div className="tiny" style={{ marginTop: 3 }}>
                  {deck.slide_count} {deck.slide_count === 1 ? "slide" : "slides"} · edited{" "}
                  {relative(deck.updated_at)}
                </div>
              </div>
              <Link to={`/admin/${deck.id}`} className="btn btn-outline">
                Edit
              </Link>
              <button className="btn btn-text btn-danger" onClick={() => void remove(deck)}>
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
